import React, { useEffect, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, AlertIcon, Button, Heading, Text, Tooltip, VStack } from '@chakra-ui/react';
import { Mutation, MutationPollSmsLoginBrowserArgs, MutationRequestSmsLoginLinkArgs } from '../../../generated/graphql';
import { POLL_SMS_LOGIN_BROWSER, REQUEST_SMS_LOGIN_LINK } from '../queries';
import { resumeEmailLinkDestination } from '../utils/emailLinkNavigation';
import { PhoneInputForm } from './PhoneInputForm';

/** Reuse the module's country/phone controls for the tenant-selected SMS experience. */
export function SmsLinkSignIn(props: React.ComponentProps<typeof PhoneInputForm>) {
  const apollo = useApolloClient();
  const returnPath = props.returnPath || '/';
  const [request, { loading, error }] = useMutation<Pick<Mutation, 'requestSmsLoginLink'>, MutationRequestSmsLoginLinkArgs>(REQUEST_SMS_LOGIN_LINK);
  const [pending, setPending] = useState<{ token: string; label: string; until: number }>();
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [waitingError, setWaitingError] = useState('');
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  useEffect(() => {
    if (!pending) return;
    const waiting = pending;
    let stopped = false;
    let timer: number;
    const navigate = resumeEmailLinkDestination;
    const onStorage = (event: StorageEvent) => { if (event.key === 'auth_token' && event.newValue) navigate(returnPath); };
    window.addEventListener('storage', onStorage);
    async function poll() {
      if (stopped) return;
      if (localStorage.getItem('auth_token')) { navigate(returnPath); return; }
      if (Date.now() >= waiting.until) { setWaitingError('This request has expired. Request a new SMS link.'); return; }
      try {
        const { data } = await apollo.mutate<Pick<Mutation, 'pollSmsLoginBrowser'>, MutationPollSmsLoginBrowserArgs>({ mutation: POLL_SMS_LOGIN_BROWSER, variables: { token: waiting.token } });
        if (stopped) return;
        if (data?.pollSmsLoginBrowser.auth?.token) {
          if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', data.pollSmsLoginBrowser.auth.token);
          navigate(data.pollSmsLoginBrowser.returnPath || returnPath);
          return;
        }
        if (data?.pollSmsLoginBrowser.status === 'EXPIRED') { setWaitingError('This request is no longer available. Request a new SMS link.'); return; }
        setWaitingError('');
      } catch { if (!stopped) setWaitingError('Unable to check sign-in yet. Retrying automatically…'); }
      if (!stopped) timer = window.setTimeout(poll, 3000);
    }
    timer = window.setTimeout(poll, 3000);
    return () => { stopped = true; window.clearTimeout(timer); window.removeEventListener('storage', onStorage); };
  }, [apollo, pending, returnPath]);
  async function send() {
    if (loading || retryAt > Date.now()) return;
    try {
      const { data } = await request({ variables: { input: { phoneNumber: props.phoneNumber, returnPath } } });
      const response = data?.requestSmsLoginLink;
      if (!response?.accepted) return;
      setPending({ token: response.browserToken, label: response.browserLabel, until: Date.now() + 600000 });
      localStorage.setItem('sms_login_browser_label', response.browserLabel);
      setRetryAt(Date.now() + response.retryAfterSeconds * 1000);
      setWaitingError('');
    } catch { /* The mutation error remains visible. */ }
  }
  if (!pending) return <PhoneInputForm {...props} sendLabel="Send sign-in link" loading={loading} error={error?.message || props.error} onSendCode={send} />;
  const cooldown = Math.max(0, Math.ceil((retryAt - now) / 1000));
  return <VStack align="stretch" spacing={5}>
    <Heading size="md">Check your messages</Heading>
    <Text>If sign-in is available for {props.phoneNumber}, an SMS link will arrive. Open it to continue without entering a code.</Text>
    <Text>If you open it in a different browser, approve browser <strong>{pending.label}</strong> there to sign in here too. This page will then continue automatically.</Text>
    {(waitingError || error) && <Alert status="warning"><AlertIcon />{waitingError || error?.message}</Alert>}
    <Tooltip label="Send a new ten-minute SMS link. The previous link stops working."><Button isLoading={loading} isDisabled={cooldown > 0} onClick={send}>{cooldown ? `Resend in ${cooldown}s` : 'Resend SMS link'}</Button></Tooltip>
    <Tooltip label="Enter another number. This does not change the phone number saved on an account."><Button variant="ghost" onClick={() => { setPending(undefined); setWaitingError(''); setRetryAt(0); }}>Change mobile number</Button></Tooltip>
    <Text fontSize="sm" role="status">This request does not confirm delivery. Links work once and expire in ten minutes.</Text>
  </VStack>;
}
